import {useLayoutEffect, useRef, useState} from 'react';

import {SystemHealth} from '~/modules/api';
import {RAG_COLOR, isWeekendWindow, tooltipText} from './health.utils';

const W = 340;
const H = 132;
const PAD = {t: 8, r: 6, b: 26, l: 34};

const COLORS = {
  line: '#2f6fed',
  grid: '#e4e9ef',
  weekend: '#fff3e6',
  muted: '#6b7a8c',
};

type Props = {
  points: SystemHealth.SystemHealthHistoryPoint[];
  thresholds: SystemHealth.RagThresholds;
  fromZero: boolean;
};

/**
 * Line chart with weekend bands, a RAG status strip along the bottom, and
 * per-day RAG-coloured dots, driven by server-computed RAG.
 */
export const HealthChart = ({points, thresholds, fromZero}: Props) => {
  const [tip, setTip] = useState<{x: number; y: number; text: string} | null>(
    null,
  );

  const numeric = points.filter(p => typeof p.value === 'number');
  if (numeric.length < 2 || points.length < 2) {
    return (
      <div className="h-[132px] flex items-center justify-center text-xs text-gray-400">
        not enough data to chart
      </div>
    );
  }

  const vals = numeric.map(p => p.value as number);
  const lo = fromZero ? 0 : Math.min(...vals);
  let hi = Math.max(...vals);
  if (hi === lo) hi = lo + 1;

  const x = (i: number) =>
    PAD.l + (i / (points.length - 1)) * (W - PAD.l - PAD.r);
  const y = (v: number) =>
    PAD.t + (1 - (v - lo) / (hi - lo)) * (H - PAD.t - PAD.b);
  const bandW = (W - PAD.l - PAD.r) / (points.length - 1);

  const fmtAxis = (v: number) =>
    v >= 1000
      ? (v / 1000).toFixed(1).replace(/\.0$/, '') + 'k'
      : String(Math.round(v));

  // line path, broken across gaps
  let d = '';
  let pen = false;
  points.forEach((p, i) => {
    if (typeof p.value === 'number') {
      d += `${pen ? 'L' : 'M'}${x(i).toFixed(1)} ${y(p.value).toFixed(1)}`;
      pen = true;
    } else {
      pen = false;
    }
  });

  return (
    <div className="relative">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="block w-full h-[132px]"
        onMouseLeave={() => setTip(null)}>
        {points.map((p, i) =>
          isWeekendWindow(p.date) ? (
            <rect
              key={`wk-${p.date}`}
              x={x(i) - bandW / 2}
              y={PAD.t}
              width={bandW}
              height={H - PAD.t - PAD.b}
              fill={COLORS.weekend}
            />
          ) : null,
        )}
        {[lo, (lo + hi) / 2, hi].map(v => (
          <g key={`grid-${v}`}>
            <line
              x1={PAD.l}
              x2={W - PAD.r}
              y1={y(v)}
              y2={y(v)}
              stroke={COLORS.grid}
              strokeWidth={1}
            />
            <text
              x={PAD.l - 4}
              y={y(v) + 3.5}
              textAnchor="end"
              fontSize={9}
              fill={COLORS.muted}>
              {fmtAxis(v)}
            </text>
          </g>
        ))}
        <path
          d={d}
          fill="none"
          stroke={COLORS.line}
          strokeWidth={2}
          strokeLinejoin="round"
        />
        {/* RAG status strip along the bottom */}
        {points.map((p, i) => (
          <rect
            key={`rag-${p.date}`}
            x={x(i) - bandW / 2 + 0.5}
            y={H - PAD.b + 8}
            width={Math.max(bandW - 1, 1.5)}
            height={6}
            rx={1.5}
            fill={RAG_COLOR[p.status] ?? 'transparent'}
          />
        ))}
        {/* dots + hover targets */}
        {points.map((p, i) => {
          if (typeof p.value !== 'number') return null;
          const alert = p.status === 'red' || p.status === 'amber';
          return (
            <g key={`pt-${p.date}`}>
              <circle
                cx={x(i)}
                cy={y(p.value)}
                r={alert ? 3.4 : 2.4}
                fill={RAG_COLOR[p.status] ?? COLORS.line}
              />
              <rect
                x={x(i) - 6}
                y={0}
                width={12}
                height={H}
                fill="transparent"
                onMouseMove={e =>
                  setTip({
                    x: e.clientX + 12,
                    y: e.clientY + 12,
                    text: tooltipText(p, thresholds),
                  })
                }
              />
            </g>
          );
        })}
      </svg>
      {tip && <Tooltip x={tip.x} y={tip.y} text={tip.text} />}
    </div>
  );
};

const TIP_MARGIN = 8;

/**
 * Fixed-position tooltip that stays inside the viewport: once rendered it
 * measures itself and flips to the left of / above the cursor when the
 * default bottom-right placement would run off screen.
 */
const Tooltip = ({x, y, text}: {x: number; y: number; text: string}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({left: x, top: y});

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const {width, height} = el.getBoundingClientRect();
    const maxLeft = window.innerWidth - width - TIP_MARGIN;
    const maxTop = window.innerHeight - height - TIP_MARGIN;
    // the cursor sits 12px up-left of (x, y); when flipping, keep the same
    // clearance on the other side so the tip never covers the pointer
    const left = x > maxLeft ? Math.max(TIP_MARGIN, x - 24 - width) : x;
    const top = y > maxTop ? Math.max(TIP_MARGIN, y - 24 - height) : y;
    setPos({left, top});
  }, [x, y, text]);

  return (
    <div
      ref={ref}
      className="fixed z-10 pointer-events-none whitespace-pre rounded-md bg-gray-800 text-white text-xs px-2 py-1"
      style={pos}>
      {text}
    </div>
  );
};
